/**
 * Pure derivation of the Settings navigation from the server manifest.
 *
 * Kept apart from the React components so it can be tested directly, and so there is one obvious
 * place to look for "how does navigation get decided". The answer is: it doesn't get decided
 * here. The server already said which sections this person is offered; this only groups and
 * labels them.
 */
import type { SettingsManifest } from '@/lib/settingsApi';
import { GROUP_ORDER, sectionMeta, type SettingsGroup } from './catalogue';

export interface NavGroup {
  group: SettingsGroup;
  /** What the band is called on screen. Says "view only" when nothing in it can be changed. */
  heading: string;
  sections: Array<{ id: string; label: string }>;
}

/**
 * Sections grouped for navigation, in registry order within each group.
 *
 * A band whose sections are ALL read-only is labelled as such. Without it, a chambers barrister
 * and the chambers administrator see a nearly identical sidebar, and the difference between
 * "these are my settings" and "this is my chambers' configuration, which I am only looking at"
 * is only discoverable by opening a section and finding nothing to change.
 *
 * The distinction is drawn from the manifest's own data — whether anything in the band is
 * writable — not from who the person is. Nothing here needs to know their role.
 */
export function groupSections(manifest: SettingsManifest): NavGroup[] {
  const groups = new Map<SettingsGroup, NavGroup['sections']>();
  const writable = new Map<SettingsGroup, boolean>();

  for (const section of manifest.sections) {
    const meta = sectionMeta(section.id);
    const list = groups.get(meta.group) ?? [];
    list.push({ id: section.id, label: meta.label });
    groups.set(meta.group, list);
    writable.set(meta.group, (writable.get(meta.group) ?? false) || section.hasWritable);
  }

  return GROUP_ORDER.filter((g) => groups.has(g)).map((g) => ({
    group: g,
    heading: writable.get(g) ? g : `${g} · view only`,
    sections: groups.get(g)!,
  }));
}

/**
 * Where Settings lives in the URL, from the SERVER-RESOLVED actor.
 *
 * This is the ONE place in the Settings feature that branches on who someone is, and it decides
 * a URL prefix — not a permission. The prefix exists because the application's routes are
 * role-scoped (the basePath convention); nothing here grants or withholds anything, and the
 * value it branches on comes from the manifest, never from the client's own reading of the user.
 */
export function settingsBasePath(actor: SettingsManifest['context']['actor']): string {
  if (actor === 'clerk') return '/clerk/settings';
  if (actor === 'admin') return '/admin/settings';
  if (actor === 'barrister') return '/barrister/settings';
  // Staff have personal settings but no role-scoped area of the application — the /barrister
  // routes are gated to practitioners, so sending them there is a door they cannot open. The
  // role-agnostic mount is their home.
  return '/settings';
}

/** One line describing the workspace this person is configuring. */
export function contextLine(manifest: SettingsManifest): string {
  const { workspaceType, actor, isOwner, supportsBarristerCount } = manifest.context;
  if (actor === 'clerk') {
    const n = supportsBarristerCount;
    const who = n === 0 ? 'no barristers yet' : n === 1 ? '1 barrister' : `${n} barristers`;
    return `Your account and the way you work — supporting ${who}`;
  }
  if (isOwner && workspaceType === 'solo') return 'Your account, your practice, and how it runs';
  if (isOwner) return 'Your account, and the chambers you administer';
  if (actor === 'barrister') return 'Your account and your practice. Chambers settings are read-only.';
  return 'Your account and your preferences';
}

/** How a section summarises itself on the overview: what is editable, out of what is there. */
export function sectionSummary(section: SettingsManifest['sections'][number]): string {
  const editable = section.settings.filter((s) => s.canWrite).length;
  const total = section.settings.length;
  if (editable === 0) return `${total} ${total === 1 ? 'setting' : 'settings'} · view only`;
  return `${editable} of ${total} editable`;
}
